import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

export type ToastVariant = "success" | "error" | "info";

export interface ToastMessage {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  showToast(input: Omit<ToastMessage, "id">): string;
  dismissToast(id: string): void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

interface ToastProviderProps {
  children: ReactNode;
}

const TOAST_DURATION_MS = 2800;

function variantClass(variant: ToastVariant): string {
  if (variant === "success") return "border-emerald-300 bg-emerald-50 text-emerald-900";
  if (variant === "error") return "border-red-300 bg-red-50 text-red-900";
  return "border-border bg-background text-foreground";
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const timeoutRef = useRef<Map<string, number>>(new Map());

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
    const timeout = timeoutRef.current.get(id);
    if (timeout !== undefined) {
      window.clearTimeout(timeout);
      timeoutRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (input: Omit<ToastMessage, "id">) => {
      const id = crypto.randomUUID();
      setToasts(prev => [...prev, { ...input, id }]);
      const timeout = window.setTimeout(() => {
        dismissToast(id);
      }, TOAST_DURATION_MS);
      timeoutRef.current.set(id, timeout);
      return id;
    },
    [dismissToast],
  );

  useEffect(() => {
    return () => {
      for (const timeout of timeoutRef.current.values()) {
        window.clearTimeout(timeout);
      }
      timeoutRef.current.clear();
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      showToast,
      dismissToast,
    }),
    [dismissToast, showToast],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 mx-auto flex w-full max-w-md flex-col gap-2 px-4" aria-live="polite" aria-atomic="false">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`toast-enter pointer-events-auto rounded-lg border px-4 py-3 shadow-lg ${variantClass(toast.variant)}`}
            role={toast.variant === "error" ? "alert" : "status"}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="grid gap-1">
                <p className="text-sm font-medium">{toast.title}</p>
                {toast.description ? <p className="text-xs opacity-90">{toast.description}</p> : null}
              </div>
              <button
                type="button"
                className="text-xs opacity-75 hover:opacity-100"
                onClick={() => dismissToast(toast.id)}
                aria-label="Dismiss notification"
              >
                Dismiss
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
